import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import venueDashboardApiService from '../../../services/venueDashboardApi';
import {
  connectGoogleCalendar,
  listGoogleCalendars,
  parseGooglePushMessage
} from '../../../services/calendarSyncClient';
import { parseCalendarListError } from '../../../utils/calendarSyncErrors';
import { findSpaceById, resolveApiSpaceId } from '../utils/venueSpacesUi';
import { getSpaceOptionId } from './SpaceSwitcher';
import '../styles/CalendarSyncSettings.css';

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

const CalendarSyncSettings = ({
  venueId,
  venueDetails,
  settingsSpaces = [],
  settingsSpaceId,
  syncCalendar,
  syncLoading,
  pushCalendarToGoogle,
  pushLoading = false,
  onNotify
}) => {
  const { currentUser } = useAuth();
  const [maps, setMaps] = useState([]);
  const [mapsLoading, setMapsLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [customEmail, setCustomEmail] = useState('');
  const [calendars, setCalendars] = useState([]);
  const [calendarsLoading, setCalendarsLoading] = useState(false);
  const [selectedCalendarId, setSelectedCalendarId] = useState('');
  const [connectLoading, setConnectLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [manualCalendarId, setManualCalendarId] = useState('');
  const [manualCalendarLabel, setManualCalendarLabel] = useState('');

  const venueContactEmail = normalizeEmail(
    venueDetails?.contact_email || venueDetails?.contactEmail
  );
  const loggedInEmail = normalizeEmail(currentUser?.email);
  const defaultSyncEmail = venueContactEmail || loggedInEmail;
  const scopedSpaceId =
    settingsSpaceId || getSpaceOptionId(settingsSpaces?.[0]) || null;
  const apiSpaceId = resolveApiSpaceId(scopedSpaceId);
  const activeSpace = findSpaceById(settingsSpaces, scopedSpaceId);

  const knownEmails = useMemo(() => {
    const set = new Set();
    if (defaultSyncEmail) set.add(defaultSyncEmail);
    if (loggedInEmail) set.add(loggedInEmail);
    if (venueContactEmail) set.add(venueContactEmail);
    maps.forEach((m) => {
      const e = normalizeEmail(m.email);
      if (e) set.add(e);
    });
    return [...set].sort();
  }, [maps, venueContactEmail, defaultSyncEmail, loggedInEmail]);

  const spaceMaps = useMemo(
    () => maps.filter((m) => apiSpaceId && String(m.space_id) === String(apiSpaceId)),
    [maps, apiSpaceId]
  );

  const loadMaps = useCallback(async () => {
    if (!venueId) return;
    setMapsLoading(true);
    try {
      const rows = await venueDashboardApiService.getCalendarMaps(venueId);
      setMaps(Array.isArray(rows) ? rows : []);
    } catch (error) {
      onNotify?.({ type: 'error', title: 'Could not load calendar maps', message: error.message });
    } finally {
      setMapsLoading(false);
    }
  }, [venueId, onNotify]);

  useEffect(() => {
    void loadMaps();
  }, [loadMaps]);

  useEffect(() => {
    if (selectedEmail) return;
    if (defaultSyncEmail) {
      setSelectedEmail(defaultSyncEmail);
      return;
    }
    if (knownEmails.length > 0) setSelectedEmail(knownEmails[0]);
  }, [knownEmails, selectedEmail, defaultSyncEmail]);

  const refreshCalendars = useCallback(async (email, { notifyOnError = true } = {}) => {
    const target = normalizeEmail(email);
    if (!target) return false;
    setCalendarsLoading(true);
    setCalendars([]);
    setSelectedCalendarId('');
    setListError(null);
    try {
      const list = await listGoogleCalendars(target);
      setCalendars(list);
      if (list.length === 1) setSelectedCalendarId(list[0].id);
      return true;
    } catch (error) {
      const parsed = parseCalendarListError(error.rawDetail || error.message);
      setListError(parsed);
      if (notifyOnError) {
        onNotify?.({
          type: parsed.code === 'INSUFFICIENT_SCOPES' ? 'warning' : 'info',
          title: parsed.title,
          message: parsed.message.replace(/\*\*/g, '')
        });
      }
      return false;
    } finally {
      setCalendarsLoading(false);
    }
  }, [onNotify]);

  const handleConnectGoogle = async () => {
    const email = normalizeEmail(customEmail || selectedEmail);
    if (!email) {
      onNotify?.({ type: 'error', title: 'Enter a Google account email' });
      return;
    }
    setConnectLoading(true);
    try {
      await connectGoogleCalendar(email);
      setSelectedEmail(email);
      setCustomEmail('');
      onNotify?.({ type: 'success', title: 'Google Calendar connected', message: email });
      const listed = await refreshCalendars(email, { notifyOnError: false });
      if (!listed) {
        onNotify?.({
          type: 'success',
          title: 'Google connected',
          message: 'Account linked. Click Refresh list to load your calendars.'
        });
      }
    } catch (error) {
      onNotify?.({ type: 'error', title: 'Connection failed', message: error.message });
    } finally {
      setConnectLoading(false);
    }
  };

  const handleAddEmail = () => {
    const email = normalizeEmail(customEmail);
    if (!email) return;
    setSelectedEmail(email);
    setCustomEmail('');
  };

  const handleSaveMap = async () => {
    if (!venueId || !apiSpaceId) {
      onNotify?.({ type: 'error', title: 'Select a space first' });
      return;
    }
    const email = normalizeEmail(selectedEmail);
    const calendarId = String(selectedCalendarId || manualCalendarId || '').trim();
    if (!email || !calendarId) {
      onNotify?.({ type: 'error', title: 'Pick a Google account and calendar (or enter calendar ID)' });
      return;
    }
    const cal = calendars.find((c) => c.id === calendarId);
    const calendarSummary =
      cal?.summary || manualCalendarLabel.trim() || calendarId;
    setSaveLoading(true);
    try {
      if (!venueDetails?.multi_space_calendars_enabled && !venueDetails?.multiSpaceCalendarsEnabled) {
        await venueDashboardApiService.setMultiSpaceCalendarsEnabled(venueId, true);
      }
      await venueDashboardApiService.createCalendarMap(venueId, {
        space_id: apiSpaceId,
        email,
        calendar_id: calendarId,
        provider: 'google',
        calendar_summary: calendarSummary
      });
      await loadMaps();
      onNotify?.({
        type: 'success',
        title: 'Calendar linked',
        message: `${activeSpace?.name || 'Space'} → ${calendarSummary}`
      });
      setSelectedCalendarId('');
      setManualCalendarId('');
      setManualCalendarLabel('');
    } catch (error) {
      onNotify?.({ type: 'error', title: 'Could not save map', message: error.message });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleRemoveMap = async (mapId) => {
    if (!venueId || !mapId) return;
    try {
      await venueDashboardApiService.deleteCalendarMap(venueId, mapId);
      await loadMaps();
      onNotify?.({ type: 'success', title: 'Calendar link removed' });
    } catch (error) {
      onNotify?.({ type: 'error', title: 'Could not remove map', message: error.message });
    }
  };

  const handleSyncNow = async () => {
    const email = normalizeEmail(selectedEmail);
    if (!syncCalendar) return;
    await syncCalendar({
      email: email || undefined,
      spaceId: apiSpaceId || undefined
    });
  };

  const handlePushNow = async () => {
    const email = normalizeEmail(selectedEmail);
    if (!pushCalendarToGoogle) return;
    try {
      const result = await pushCalendarToGoogle({
        email: email || undefined,
        spaceId: apiSpaceId || undefined
      });
      if (result) {
        const written = (result.pushed ?? 0) + (result.updated ?? 0);
        const fallback =
          `Created ${result.pushed ?? 0}, updated ${result.updated ?? 0}${
            result.failed ? `, ${result.failed} failed` : ''
          }.`;
        const { body } = parseGooglePushMessage(
          result.message || '',
          result.googleEventHtmlLink
        );
        onNotify?.({
          type: written > 0 && !result.failed ? 'success' : 'warning',
          title: written > 0 ? 'Pushed to Google' : 'Nothing pushed',
          message: result.message ? body || fallback : fallback
        });
      }
    } catch (error) {
      onNotify?.({ type: 'error', title: 'Push failed', message: error.message });
    }
  };

  return (
    <div className="calendar-sync-settings">
      <header className="calendar-sync-settings-header">
        <h3 className="calendar-sync-settings-title">Calendar sync</h3>
        <p className="calendar-sync-settings-lead">
          Link each space to a Google calendar. You can use different calendars on the same Google
          account, or connect another account per space. Sync imports events only from mapped
          calendars.
        </p>
      </header>

      <section className="calendar-sync-settings-section">
        <h4>Google account</h4>
        <p className="calendar-sync-settings-hint">
          Step 1: pick the Google account email below. Step 2: click <strong>Connect Google</strong>{' '}
          and finish consent in the popup (allow popups for localhost). Step 3: click{' '}
          <strong>Refresh list</strong>. Venue contact: {venueContactEmail || '—'}
        </p>
        <div className="calendar-sync-settings-row">
          <select
            className="calendar-sync-settings-select"
            value={selectedEmail}
            onChange={(e) => setSelectedEmail(e.target.value)}
          >
            {knownEmails.length === 0 ? (
              <option value="">Enter your Google email below</option>
            ) : (
              knownEmails.map((email) => (
                <option key={email} value={email}>
                  {email}
                </option>
              ))
            )}
          </select>
          <button
            type="button"
            className="btn-secondary"
            disabled={connectLoading || !selectedEmail}
            onClick={() => void refreshCalendars(selectedEmail, { notifyOnError: true })}
          >
            {calendarsLoading ? 'Loading…' : 'Refresh list'}
          </button>
        </div>
        <div className="calendar-sync-settings-row">
          <input
            type="email"
            className="calendar-sync-settings-input"
            placeholder={defaultSyncEmail || 'Google account email (e.g. you@gmail.com)'}
            value={customEmail}
            onChange={(e) => setCustomEmail(e.target.value)}
          />
          <button type="button" className="btn-secondary" onClick={handleAddEmail}>
            Use email
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={connectLoading}
            onClick={() => void handleConnectGoogle()}
          >
            {connectLoading ? 'Connecting…' : 'Connect Google'}
          </button>
        </div>
      </section>

      <section className="calendar-sync-settings-section">
        <h4>
          Map calendar — {activeSpace?.name || 'Select a space above'}
        </h4>
        {!apiSpaceId ? (
          <p className="calendar-sync-settings-hint">Choose a space in the bar above.</p>
        ) : (
          <>
            {spaceMaps.length > 0 ? (
              <ul className="calendar-sync-map-list">
                {spaceMaps.map((m) => (
                  <li key={m.id} className="calendar-sync-map-item">
                    <span>
                      <strong>{m.calendar_summary || m.calendar_id}</strong>
                      <span className="calendar-sync-map-meta"> · {m.email}</span>
                    </span>
                    <button
                      type="button"
                      className="btn-text-danger"
                      onClick={() => void handleRemoveMap(m.id)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="calendar-sync-settings-hint">No calendar linked for this space yet.</p>
            )}

            {listError?.code === 'INSUFFICIENT_SCOPES' ? (
              <div className="calendar-sync-scope-warning" role="status">
                <p>
                  <strong>Calendar list blocked by Google scopes.</strong> Prod OAuth currently requests
                  only free/busy scopes, not calendar list. Ops: set{' '}
                  <code>USE_FULL_CALENDAR_SCOPE=true</code> on the calendar Lambda, redeploy, then{' '}
                  <strong>Connect Google</strong> again. Until then, paste the calendar ID from Google
                  Calendar → Settings → your calendar → Integrate calendar.
                </p>
              </div>
            ) : null}

            <div className="calendar-sync-settings-row">
              <select
                className="calendar-sync-settings-select calendar-sync-settings-select--wide"
                value={selectedCalendarId}
                onChange={(e) => setSelectedCalendarId(e.target.value)}
                disabled={calendarsLoading || calendars.length === 0}
              >
                <option value="">
                  {calendarsLoading
                    ? 'Loading calendars…'
                    : calendars.length
                      ? 'Select a Google calendar'
                      : 'Connect Google, then refresh list'}
                </option>
                {calendars.map((cal) => (
                  <option key={cal.id} value={cal.id}>
                    {cal.summary || cal.id}
                    {cal.primary ? ' (primary)' : ''}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn-primary"
                disabled={saveLoading || !(selectedCalendarId || manualCalendarId.trim())}
                onClick={() => void handleSaveMap()}
              >
                {saveLoading ? 'Saving…' : 'Link to this space'}
              </button>
            </div>

            <div className="calendar-sync-manual-id">
              <p className="calendar-sync-settings-hint">
                Or enter calendar ID manually (e.g. <code>abc@group.calendar.google.com</code> or your
                Gmail address for the primary calendar):
              </p>
              <div className="calendar-sync-settings-row">
                <input
                  type="text"
                  className="calendar-sync-settings-input"
                  placeholder="Calendar ID"
                  value={manualCalendarId}
                  onChange={(e) => setManualCalendarId(e.target.value)}
                />
                <input
                  type="text"
                  className="calendar-sync-settings-input"
                  placeholder="Display name (optional)"
                  value={manualCalendarLabel}
                  onChange={(e) => setManualCalendarLabel(e.target.value)}
                />
              </div>
            </div>
          </>
        )}
      </section>

      <section className="calendar-sync-settings-section calendar-sync-settings-actions">
        <div className="calendar-sync-settings-row calendar-sync-settings-row--actions">
          <button
            type="button"
            className="btn-primary"
            disabled={syncLoading || mapsLoading || maps.length === 0}
            onClick={() => void handleSyncNow()}
          >
            {syncLoading ? 'Syncing…' : 'Import from Google'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={pushLoading || mapsLoading || maps.length === 0 || !pushCalendarToGoogle}
            onClick={() => void handlePushNow()}
          >
            {pushLoading ? 'Pushing…' : 'Push Occasia to Google'}
          </button>
        </div>
        {maps.length === 0 ? (
          <p className="calendar-sync-settings-hint">
            Link at least one calendar to a space before importing or pushing.
          </p>
        ) : (
          <p className="calendar-sync-settings-hint">
            <strong>Import</strong> pulls external Google events into Occasia (events Occasia already
            pushed are ignored so you do not double-block). <strong>Push</strong> sends Occasia
            bookings and manual blocks to the mapped calendar. Use import for outside holds; use push
            after you change bookings in Occasia. Both refresh the timeline—no separate refresh
            button needed.
          </p>
        )}
      </section>
    </div>
  );
};

export default CalendarSyncSettings;
